/**
 * Quick add for recommendation tiles (snippets/recommendation-card.liquid).
 *
 * One native <dialog> serves every rail (PDP, Explore more, cart drawer, cart page). It is shown modally, so the
 * rest of the page, including an open cart drawer, is inert while it's open: no second focus trap, and Escape
 * closes only the dialog. The shopper chooses every option; a value with no available variant (given the other
 * choices) is disabled. Add to cart sends exactly the chosen variant, quantity 1, at its own price.
 *
 * Cart safety (Batch 7): the add waits for any pending cart change, then claims cartChangeState so quantity changes
 * wait for it, and bumps the read ticket so an older read can't repaint over the result. A 4xx shows Shopify's
 * message. Any other failure is treated as unknown: the cart is read back (the add is never resent) and the drawer
 * or cart page repaints from Shopify's data.
 */
(function () {
  if (window.mmQuickAdd) return;

  const text = {
    add: 'Add to cart',
    soldOut: (window.variantStrings && window.variantStrings.soldOut) || 'Sold out',
    error: (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart. Please try again.',
    adding: 'Adding…',
    added: 'Added to your cart',
    choose: 'Choose',
    unconfirmed: "We couldn't confirm whether this was added. Please check your cart before trying again.",
    busyCart: 'Your cart is still updating. Please try again in a moment.',
  };

  let dialog = null;
  let product = null;
  let selected = [];
  let trigger = null;
  let busy = false;
  let lockedScroll = false;
  let swallowEscapeUp = false;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const cartState = () => (typeof cartChangeState !== 'undefined' ? cartChangeState : null);

  // ---------------------------------------------------------------- dialog markup

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'mm-qa';
    dialog.setAttribute('aria-labelledby', 'MmQuickAddTitle');
    dialog.innerHTML = `
      <div class="mm-qa__panel">
        <button type="button" class="mm-qa__close" aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
        <div class="mm-qa__head">
          <div class="mm-qa__media"><img class="mm-qa__img" alt="" width="480" height="480"></div>
          <div class="mm-qa__summary">
            <h2 class="mm-qa__title" id="MmQuickAddTitle"></h2>
            <p class="mm-qa__price"></p>
          </div>
        </div>
        <form class="mm-qa__form" novalidate>
          <div class="mm-qa__options"></div>
          <p class="mm-qa__status" role="status"></p>
          <p class="mm-qa__error" role="alert"></p>
          <button type="submit" class="mm-qa__add disable-global-button-styling" aria-disabled="true">
            <span class="mm-qa__add-label"></span>
          </button>
          <a class="mm-qa__details" href="">View full details</a>
        </form>
      </div>`;
    document.body.appendChild(dialog);

    dialog.querySelector('.mm-qa__close').addEventListener('click', () => close());
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      if (busy) return;
      // Escape closes on keydown and focus goes back to the trigger; when that's in the cart drawer, the keyup would
      // reach the drawer's own Escape handler and close it too, so that one keyup is swallowed
      swallowEscapeUp = true;
      setTimeout(() => (swallowEscapeUp = false), 600);
      close();
    });
    document.addEventListener(
      'keyup',
      (event) => {
        if (!swallowEscapeUp || event.key !== 'Escape') return;
        swallowEscapeUp = false;
        event.stopPropagation();
      },
      true,
    );
    // A click on the backdrop (outside the panel) closes
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog || busy) return;
      const r = dialog.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) close();
    });
    dialog.querySelector('.mm-qa__options').addEventListener('change', onOptionChange);
    dialog.querySelector('.mm-qa__form').addEventListener('submit', onSubmit);
  }

  function renderOptions() {
    const container = dialog.querySelector('.mm-qa__options');
    container.innerHTML = '';
    product.options.forEach((name, index) => {
      const values = [];
      product.variants.forEach((variant) => {
        const value = variant.options[index];
        if (!values.includes(value)) values.push(value);
      });
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'mm-qa__option';
      const legend = document.createElement('legend');
      legend.className = 'mm-qa__legend';
      legend.innerHTML = `<span class="mm-qa__legend-name"></span><span class="mm-qa__legend-value"></span>`;
      legend.querySelector('.mm-qa__legend-name').textContent = name;
      fieldset.appendChild(legend);
      // Colour as swatches only when every colour has one (Shopify swatch, variant photo or the theme setting);
      // otherwise the whole option stays as readable text
      const swatches = index === product.swatchOption && product.swatches ? product.swatches : null;
      const asSwatches = !!swatches && values.every((value) => swatches[value]);
      const list = document.createElement('div');
      list.className = asSwatches ? 'mm-qa__values mm-qa__values--swatches' : 'mm-qa__values';
      values.forEach((value, valueIndex) => {
        const id = `MmQuickAdd-${index}-${valueIndex}`;
        const label = document.createElement('label');
        label.className = asSwatches ? 'mm-qa__value mm-qa__value--swatch' : 'mm-qa__value';
        label.setAttribute('for', id);
        label.innerHTML = asSwatches
          ? `<input type="radio" class="mm-qa__radio" id="${id}" name="mm-qa-option-${index}"><span class="mm-qa__swatch"><span class="mm-qa__swatch-fill"></span></span>`
          : `<input type="radio" class="mm-qa__radio" id="${id}" name="mm-qa-option-${index}"><span class="mm-qa__pill"></span>`;
        const input = label.querySelector('input');
        input.value = value;
        input.dataset.option = index;
        if (asSwatches) {
          const fill = label.querySelector('.mm-qa__swatch-fill');
          const swatch = swatches[value];
          if (swatch.image) fill.style.backgroundImage = `url("${swatch.image}")`;
          else fill.style.backgroundColor = swatch.color;
          label.title = value;
        } else {
          label.querySelector('.mm-qa__pill').textContent = value;
        }
        list.appendChild(label);
      });
      fieldset.appendChild(list);
      container.appendChild(fieldset);
    });
  }

  // ---------------------------------------------------------------- selection state

  const matches = (variant, choices) => choices.every((value, i) => value === null || variant.options[i] === value);
  const currentVariant = () =>
    selected.every((value) => value !== null) ? product.variants.find((variant) => matches(variant, selected)) || null : null;

  function update({ announce = false } = {}) {
    let cleared = null;
    product.options.forEach((_, index) => {
      // A value is possible if some available variant has it alongside the other choices made so far
      const others = selected.map((value, i) => (i === index ? null : value));
      dialog.querySelectorAll(`input[data-option="${index}"]`).forEach((input) => {
        const possible = product.variants.some(
          (variant) => variant.available && variant.options[index] === input.value && matches(variant, others),
        );
        input.disabled = !possible;
        input.closest('.mm-qa__value').classList.toggle('is-unavailable', !possible);
        input.setAttribute('aria-label', possible ? input.value : `${input.value}, ${text.soldOut.toLowerCase()}`);
        if (!possible && input.checked) {
          input.checked = false;
          selected[index] = null;
          cleared = `${input.value} ${product.options[index].toLowerCase()} isn't available with that choice.`;
        }
      });
      const legendValue = dialog.querySelectorAll('.mm-qa__legend-value')[index];
      legendValue.textContent = selected[index] ? `: ${selected[index]}` : '';
    });

    const variant = currentVariant();
    const missing = product.options.filter((_, i) => selected[i] === null);
    dialog.querySelector('.mm-qa__price').textContent = variant ? variant.price : product.price;

    // Image: the chosen variant's, else any variant matching the choices so far that has one, else the product's
    const pictured = variant && variant.image ? variant : product.variants.find((v) => v.image && matches(v, selected));
    const img = dialog.querySelector('.mm-qa__img');
    const src = (pictured && pictured.image) || product.image || '';
    if (src && img.getAttribute('src') !== src) img.src = src;
    img.hidden = !src;

    const ready = !!(variant && variant.available);
    const add = dialog.querySelector('.mm-qa__add');
    add.setAttribute('aria-disabled', String(!ready));
    add.querySelector('.mm-qa__add-label').textContent = variant && !variant.available ? text.soldOut : text.add;

    const status = dialog.querySelector('.mm-qa__status');
    if (cleared) status.textContent = cleared;
    else if (announce && missing.length) status.textContent = `${text.choose} ${missing.join(' and ').toLowerCase()}.`;
    else if (!missing.length) status.textContent = '';
  }

  function onOptionChange(event) {
    const input = event.target.closest('.mm-qa__radio');
    if (!input) return;
    selected[Number(input.dataset.option)] = input.value;
    dialog.querySelector('.mm-qa__error').textContent = '';
    update();
  }

  // ---------------------------------------------------------------- open / close

  function open(button) {
    let data;
    try {
      data = JSON.parse(button.closest('.mm-card').querySelector('[data-quick-add-json]').textContent);
    } catch (error) {
      console.error(error);
      return;
    }
    if (!dialog) build();
    product = data;
    trigger = button;
    // Nothing is chosen for the shopper, except an option that has only one value
    selected = product.options.map((_, index) => {
      const values = new Set(product.variants.map((variant) => variant.options[index]));
      return values.size === 1 ? [...values][0] : null;
    });

    dialog.querySelector('.mm-qa__title').textContent = product.title;
    dialog.querySelector('.mm-qa__details').href = product.url;
    dialog.querySelector('.mm-qa__status').textContent = '';
    dialog.querySelector('.mm-qa__error').textContent = '';
    renderOptions();
    selected.forEach((value, index) => {
      if (value === null) return;
      const input = [...dialog.querySelectorAll(`input[data-option="${index}"]`)].find((i) => i.value === value);
      if (input) input.checked = true;
    });
    update();

    if (!document.body.classList.contains('overflow-hidden')) {
      document.body.classList.add('overflow-hidden');
      lockedScroll = true;
    }
    dialog.showModal();
    const first = dialog.querySelector('.mm-qa__radio:not(:disabled)') || dialog.querySelector('.mm-qa__add');
    first.focus();
  }

  function close({ returnFocus = true } = {}) {
    if (!dialog || !dialog.open) return;
    dialog.close();
    if (lockedScroll) {
      document.body.classList.remove('overflow-hidden');
      lockedScroll = false;
    }
    if (returnFocus && trigger && trigger.isConnected) trigger.focus();
  }

  // ---------------------------------------------------------------- add to cart

  async function waitForCartIdle() {
    const state = cartState();
    for (let waited = 0; state && state.pending && waited < 10000; waited += 100) await sleep(100);
    return !(state && state.pending);
  }

  function claimCart() {
    const state = cartState();
    if (!state) return;
    state.pending = true;
    state.ticket += 1;
  }

  function releaseCart() {
    const state = cartState();
    if (!state) return;
    state.pending = false;
    const queued = [...state.refreshQueue];
    state.refreshQueue.clear();
    queued.forEach((cartItems) => cartItems.onCartUpdate());
  }

  const readCart = () =>
    fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' }).then((response) =>
      response.ok ? response.json() : Promise.reject(new Error(`Cart read failed (HTTP ${response.status})`)),
    );
  const quantityOf = (cart, variantId) =>
    cart.items.filter((item) => item.variant_id === variantId).reduce((sum, item) => sum + item.quantity, 0);

  function setBusy(state) {
    busy = state;
    const add = dialog.querySelector('.mm-qa__add');
    add.classList.toggle('is-loading', state);
    if (state) {
      add.setAttribute('aria-disabled', 'true');
      add.querySelector('.mm-qa__add-label').textContent = text.adding;
    } else {
      update();
    }
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (busy) return;
    const variant = currentVariant();
    if (!variant || !variant.available) {
      update({ announce: true });
      const firstOpen = product.options.findIndex((_, i) => selected[i] === null);
      const target = firstOpen >= 0 && dialog.querySelector(`input[data-option="${firstOpen}"]:not(:disabled)`);
      if (target) target.focus();
      return;
    }

    setBusy(true);
    dialog.querySelector('.mm-qa__error').textContent = '';
    const drawer = document.querySelector('cart-drawer');
    const cartPage = document.querySelector('cart-items#main-cart-items, cart-items');
    let claimed = false;

    try {
      if (!(await waitForCartIdle())) {
        dialog.querySelector('.mm-qa__error').textContent = text.busyCart;
        return;
      }
      claimCart();
      claimed = true;

      // What the cart holds of this variant now, to judge an unconfirmed reply without resending
      const before = await readCart()
        .then((cart) => quantityOf(cart, variant.id))
        .catch(() => null);

      const sections = drawer && !cartPage ? drawer.getSectionsToRender().map((section) => section.id) : [];
      let reply = null;
      let confirmed = false;
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const body = await response.text();
        try {
          reply = JSON.parse(body);
        } catch (error) {}
        if (response.status >= 400 && response.status < 500) {
          // Rejected: nothing was added
          const message = reply && (reply.description || reply.message);
          dialog.querySelector('.mm-qa__error').textContent =
            (typeof message === 'string' && message.trim()) || text.error;
          return;
        }
        if (!response.ok || !reply || !(reply.items || reply.key || reply.id)) {
          throw new Error(`Unconfirmed add (HTTP ${response.status})`);
        }
        confirmed = true;
      } catch (error) {
        console.error(error);
        const after = await readCart()
          .then((cart) => quantityOf(cart, variant.id))
          .catch(() => null);
        confirmed = before !== null && after !== null && after > before;
        if (!confirmed) {
          // Not seen in the cart (or the cart couldn't be read): say so and leave the choice to the shopper
          dialog.querySelector('.mm-qa__error').textContent = text.unconfirmed;
          return;
        }
        reply = null;
      }

      // Added: close, then repaint the drawer / cart page from Shopify's data
      const label = `${text.added}: ${product.title}${variant.title && variant.title !== 'Default Title' ? `, ${variant.title}` : ''}.`;
      close({ returnFocus: !cartPage && !(drawer && drawer.contains(trigger)) });
      releaseCart();
      claimed = false;
      await repaint(drawer, cartPage, reply, trigger);
      announce(label);
      focusAfterAdd(variant.id);
    } finally {
      if (claimed) releaseCart();
      setBusy(false);
    }
  }

  // Drawer: render Shopify's reply (opens the drawer); without one, read the drawer back. Cart page: read back.
  async function repaint(drawer, cartPage, reply, from) {
    try {
      if (cartPage) {
        await cartPage.onCartUpdate();
      } else if (drawer) {
        if (from && !drawer.contains(from)) drawer.setActiveElement(from);
        if (reply && reply.sections) drawer.renderContents(reply);
        else await drawer.refreshCartDrawer();
      }
    } catch (error) {
      console.error(error);
    }
  }

  // Focus lands on the added line when the tile that opened the dialog has gone (it's now in the cart)
  function focusAfterAdd(variantId) {
    if (trigger && trigger.isConnected && !trigger.closest('cart-drawer, #main-cart-footer')) return;
    setTimeout(() => {
      const scope = document.querySelector('#main-cart-items') || document.querySelector('cart-drawer');
      if (!scope) return;
      const row = [...scope.querySelectorAll(`[data-quantity-variant-id="${variantId}"]`)]
        .map((input) => input.closest('.cart-item'))
        .find(Boolean);
      const target =
        (row && row.querySelector('.cart-item__name')) ||
        (trigger && trigger.isConnected ? trigger : null) ||
        scope.querySelector('.drawer__inner');
      if (target) {
        if (!target.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT)$/.test(target.tagName)) target.setAttribute('tabindex', '-1');
        target.focus();
      }
    }, 450);
  }

  function announce(message) {
    let region = document.getElementById('MmQuickAddLive');
    if (!region) {
      region = document.createElement('p');
      region.id = 'MmQuickAddLive';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      document.body.appendChild(region);
    }
    region.textContent = '';
    setTimeout(() => (region.textContent = message), 50);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quick-add-open]');
    if (!button) return;
    event.preventDefault();
    open(button);
  });

  window.mmQuickAdd = { open, close };
})();
